import { useMemo, useState } from "react";
import { X } from "lucide-react";
import { DotChip } from "@/components/shared/DotChip";
import { LocalizedLink } from "@/components/LocalizedLink";
import { useLanguage } from "@/hooks/useLanguage";
import { openPortal, useBilling } from "@/hooks/useBilling";
import { billingCopyFor } from "@/lib/billing/copy";
import { bannerFor } from "@/lib/billing/view";

/**
 * One slim line above the page content: a failed subscription payment, the
 * trial's days left, or a trial that ended. Renders nothing unless billing is
 * visible (isBillingVisible), so production is unchanged while plans are drafts.
 */
export function BillingBanner() {
  const { language } = useLanguage();
  const c = billingCopyFor(language);
  const { data } = useBilling();
  const [dismissed, setDismissed] = useState(false);
  const now = useMemo(() => new Date(), []);

  if (!data || dismissed) return null;
  const banner = bannerFor(data, now);
  if (!banner.kind) return null;

  const link =
    "text-[13px] font-medium text-[hsl(var(--action))] hover:underline dark:text-primary";

  return (
    <div
      role="status"
      className="mb-6 flex items-start gap-3 rounded-lg border bg-card px-4 py-2.5"
    >
      {/* The text and link wrap among themselves; the × stays top-right on phones
          instead of dropping onto a line of its own (the Czech line is longer). */}
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-4 gap-y-1">
        {banner.kind === "pastDue" && (
          <>
            <DotChip color="red" alert className="text-[13px]">
              {c.bannerPastDue}
            </DotChip>
            {data.isOwner ? (
              <button type="button" className={link} onClick={() => openPortal(language)}>
                {c.bannerFixCard}
              </button>
            ) : (
              <span className="text-[13px] text-muted-foreground">{c.bannerPastDueMember}</span>
            )}
          </>
        )}
        {banner.kind === "trial" && (
          <>
            <DotChip
              color={(banner.days ?? 0) <= 3 ? "amber" : "blue"}
              className="text-[13px] tabular-nums text-foreground"
            >
              {c.bannerTrial(banner.days ?? 0)}
            </DotChip>
            <LocalizedLink to="/settings/billing" className={link}>
              {c.bannerChoose}
            </LocalizedLink>
          </>
        )}
        {banner.kind === "trialEnded" && (
          <>
            <DotChip color="amber" className="text-[13px] text-foreground">
              {c.bannerTrialEnded}
            </DotChip>
            <LocalizedLink to="/settings/billing" className={link}>
              {c.bannerChoose}
            </LocalizedLink>
          </>
        )}
      </div>
      {banner.kind !== "pastDue" && (
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="-my-0.5 shrink-0 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          aria-label={c.dismiss}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
